import React from 'react';

// Import configs and util modules
import {
  createCategoryForUrl,
  createSlug,
  listingSlug,
  createSubCategoryForUrl,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PARAM_TYPE_NEW,
} from '../../../util/urlHelpers';
import { ensureListing } from '../../../util/data';
import { createResourceLocatorString } from '../../../util/routes';

// Import modules from this directory
import EditListingAvailabilityPanel from './EditListingAvailabilityPanel/EditListingAvailabilityPanel';
import EditListingDetailsPanel from './EditListingDetailsPanel/EditListingDetailsPanel';
import EditListingDeliveryPanel from './EditListingDeliveryPanel/EditListingDeliveryPanel';
import EditListingLocationPanel from './EditListingLocationPanel/EditListingLocationPanel';
import EditListingPhotosPanel from './EditListingPhotosPanel/EditListingPhotosPanel';
import EditListingPricingPanel from './EditListingPricingPanel/EditListingPricingPanel';
import EditListingPricingAndStockPanel from './EditListingPricingAndStockPanel/EditListingPricingAndStockPanel';
import EditListingCategoryPanel from './EditListingCategoryPanel/EditListingCategoryPanel';

import css from './EditListingWizardTab.module.css';
import EditListingSubscriptinPlan from './EditListingSubscriptionPlan/EditListingSubscriptinPlan';

export const SUBSCRIPTION_PLAN = 'subscription-plan';
export const DETAILS = 'details';
export const PRICING = 'pricing';
export const PRICING_AND_STOCK = 'pricing-and-stock';
export const DELIVERY = 'delivery';
export const LOCATION = 'location';
export const AVAILABILITY = 'availability';
export const PHOTOS = 'photos';
export const CATEGORY = 'category';

// EditListingWizardTab component supports these tabs
export const SUPPORTED_TABS = [
  CATEGORY,
  DETAILS,
  PRICING,
  PRICING_AND_STOCK,
  DELIVERY,
  LOCATION,
  AVAILABILITY,
  PHOTOS,
];

const pathParamsToNextTab = (params, tab, marketplaceTabs) => {
  const nextTabIndex = marketplaceTabs.findIndex(s => s === tab) + 1;
  const nextTab =
    nextTabIndex < marketplaceTabs.length
      ? marketplaceTabs[nextTabIndex]
      : marketplaceTabs[marketplaceTabs.length - 1];
  return { ...params, tab: nextTab };
};

const pathParamsToPreviousTab = (params, tab, marketplaceTabs) => {
  const nextTabIndex = marketplaceTabs.findIndex(s => s === tab) - 1;
  const nextTab = nextTabIndex > 0 ? marketplaceTabs[nextTabIndex] : marketplaceTabs[0];
  return { ...params, tab: nextTab };
};

// When user has update draft listing, he should be redirected to next EditListingWizardTab
const redirectAfterDraftUpdate = (listingId, params, tab, marketplaceTabs, history, routes) => {
  const listingUUID = listingId.uuid;
  const currentPathParams = {
    ...params,
    type: LISTING_PAGE_PARAM_TYPE_DRAFT,
    id: listingUUID,
  };

  // Replace current "new" path to "draft" path.
  // Browser's back button should lead to editing current draft instead of creating a new one.
  if (params.type === LISTING_PAGE_PARAM_TYPE_NEW) {
    const draftURI = createResourceLocatorString('EditListingPage', routes, currentPathParams, {});
    history.replace(draftURI);
  }

  // Redirect to next tab
  const nextPathParams = pathParamsToNextTab(currentPathParams, tab, marketplaceTabs);
  const to = createResourceLocatorString('EditListingPage', routes, nextPathParams, {});
  history.push(to, history.location.state);
};

const goBackToPreviousStep = (listingId, params, tab, marketplaceTabs, history, routes) => {
  const listingUUID = listingId.uuid;
  const currentPathParams = {
    ...params,
    type: LISTING_PAGE_PARAM_TYPE_DRAFT,
    id: listingUUID,
  };

  if (params.type === LISTING_PAGE_PARAM_TYPE_NEW) {
    const draftURI = createResourceLocatorString('EditListingPage', routes, currentPathParams, {});
    history.replace(draftURI);
  }

  const previousPathParams = pathParamsToPreviousTab(currentPathParams, tab, marketplaceTabs);
  const to = createResourceLocatorString('EditListingPage', routes, previousPathParams, {});
  history.push(to, history.location.state);
};

/**
 * A single tab on the EditListingWizard.
 *
 * @component
 * @param {Object} props
 * @returns {JSX.Element} EditListingWizardTab component
 */
const EditListingWizardTab = props => {
  const {
    tab,
    marketplaceTabs,
    params,
    locationSearch,
    errors,
    fetchInProgress,
    newListingPublished,
    handleCreateFlowTabScrolling,
    handlePublishListing,
    history,
    images,
    listing,
    weeklyExceptionQueries,
    monthlyExceptionQueries,
    allExceptions,
    onFetchExceptions,
    onAddAvailabilityException,
    onDeleteAvailabilityException,
    onUpdateListing,
    onCreateListingDraft,
    onImageUpload,
    onManageDisableScrolling,
    onListingTypeChange,
    onRemoveImage,
    updatedTab,
    updateInProgress,
    draftingInProgress,
    tabSubmitButtonText,
    config,
    routeConfiguration,
    onUpdateListingCategory,
    selectedCategory,
    currentUser,
    changeSubscriptionURL,
    changeSubscriptionInProgress,
    changeSubscriptionError,
  } = props;
  const { type } = params;
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewListingFlow = isNewURI || isDraftURI;
  const isEditListingFlow = type === LISTING_PAGE_PARAM_TYPE_EDIT;

  const routeName = isDraftURI ? 'ListingPageVariant' : 'ListingPage';
  const titleSlug = createSlug(listing?.attributes?.title || '');
  const category = createCategoryForUrl(listing?.attributes?.publicData?.category);
  const subCategory = createSubCategoryForUrl(listing?.attributes?.publicData?.category, type);
  const url = listing.id
    ? isDraftURI
      ? createResourceLocatorString('ListingPageVariant', routeConfiguration, {
          slug: titleSlug,
          id: listing.id.uuid,
          category,
          subCategory,
          variant: 'draft',
        })
      : createResourceLocatorString('ListingPage', routeConfiguration, {
          slug: listingSlug(listing),
          category,
          subCategory,
        })
    : '';

  const currentListing = ensureListing(listing);

  // New listing flow has automatic redirects to new tab on the wizard
  // and the last panel calls publishListing API endpoint.
  const automaticRedirectsForNewListingFlow = (tab, listingId, saveAndPublish, navigateTo) => {
    if (tab !== marketplaceTabs[marketplaceTabs.length - 1] && saveAndPublish) {
      // Create listing flow: smooth scrolling polyfill to scroll to correct tab
      handleCreateFlowTabScrolling(false);

      // After successful saving of draft data, user should be redirected to next tab
      redirectAfterDraftUpdate(
        listingId,
        params,
        tab,
        marketplaceTabs,
        history,
        routeConfiguration
      );
    } else if (saveAndPublish) {
      handlePublishListing(listingId);
      if (!!navigateTo) {
        const { from, ...rest } = navigateTo;
        const to = createResourceLocatorString(from, routeConfiguration, rest, {});
        history.push(to);
      }
    } else {
      let to = createResourceLocatorString('ManageListingsPage', routeConfiguration);
      if (!!navigateTo) {
        const { from, ...rest } = navigateTo;
        to = createResourceLocatorString(from, routeConfiguration, rest, {});
      }
      history.push(to);
    }
  };

  const onCompleteEditListingWizardTab = (tab, updateValues, saveAndPublish, navigateTo) => {
    const onUpdateListingOrCreateListingDraft = isNewURI
      ? (tab, values) => onCreateListingDraft(values, config)
      : (tab, values) => onUpdateListing(tab, values, config, saveAndPublish);

    const updateListingValues = isNewURI
      ? updateValues
      : { ...updateValues, id: currentListing.id };

    return onUpdateListingOrCreateListingDraft(tab, updateListingValues)
      .then(r => {
        // In Availability tab, the submitted data (plan) is inside a modal
        // We don't redirect provider immediately after plan is set
        if (isNewListingFlow && tab !== AVAILABILITY && !isEditListingFlow) {
          const listingId = r.data.data.id;
          automaticRedirectsForNewListingFlow(tab, listingId, saveAndPublish, navigateTo);
        } else if (isEditListingFlow) {
          history.push(createResourceLocatorString('ManageListingsPage', routeConfiguration));
        }
      })
      .catch(e => {
        // No need for extra actions
      });
  };

  const onUpdateListingCategorySubmit = category => {
    onUpdateListingCategory(category);
    const to = createResourceLocatorString(
      'EditListingPage',
      routeConfiguration,
      { ...params, tab: 'details' },
      {}
    );
    history.push(to);
  };

  const isFirstTab = marketplaceTabs.indexOf(tab) === 0;

  // Payment is a step inside the wizard now, so the first step has nothing before
  // it and shows no back button. (It used to send the seller to the standalone
  // duration page, which was the step before the wizard began.)
  const onGoBackATab = isFirstTab
    ? null
    : () =>
        goBackToPreviousStep(
          currentListing.id,
          params,
          tab,
          marketplaceTabs,
          history,
          routeConfiguration
        );
  const panelProps = tab => {
    return {
      className: css.panel,
      errors,
      listing,
      panelUpdated: updatedTab === tab,
      params,
      locationSearch,
      updateInProgress,
      draftingInProgress,
      // newListingPublished and fetchInProgress are flags for the last wizard tab
      ready: newListingPublished,
      disabled: fetchInProgress,
      submitButtonText: tabSubmitButtonText,
      listingTypes: config.listing.listingTypes,
      changeSubscriptionURL,
      changeSubscriptionInProgress,
      changeSubscriptionError,
      onManageDisableScrolling,
      onSubmit: (values, saveAndPublish = true, navigateTo = null) => {
        return onCompleteEditListingWizardTab(tab, values, saveAndPublish, navigateTo);
      },
      onGoBackATab,
      onUpdateListingCategory: onUpdateListingCategorySubmit,
      selectedCategory,
      currentUser,
    };
  };

  // TODO: add missing cases for supported tabs
  switch (tab) {
    case DETAILS: {
      return (
        <EditListingDetailsPanel
          {...panelProps(DETAILS)}
          onListingTypeChange={onListingTypeChange}
          config={config}
          marketplaceCurrency={config.currency}
          listingMinimumPriceSubUnits={config.listingMinimumPriceSubUnits}
        />
      );
    }
    case PRICING_AND_STOCK: {
      return (
        <EditListingPricingAndStockPanel
          {...panelProps(PRICING_AND_STOCK)}
          marketplaceCurrency={config.currency}
          listingMinimumPriceSubUnits={config.listingMinimumPriceSubUnits}
        />
      );
    }
    case PRICING: {
      return (
        <EditListingPricingPanel
          {...panelProps(PRICING)}
          marketplaceCurrency={config.currency}
          listingMinimumPriceSubUnits={config.listingMinimumPriceSubUnits}
        />
      );
    }
    case DELIVERY: {
      return (
        <EditListingDeliveryPanel {...panelProps(DELIVERY)} marketplaceCurrency={config.currency} />
      );
    }
    case LOCATION: {
      return (
        <EditListingLocationPanel
          {...panelProps(LOCATION)}
          marketplaceCurrency={config.currency}
          listingMinimumPriceSubUnits={config.listingMinimumPriceSubUnits}
        />
      );
    }
    case AVAILABILITY: {
      return (
        <EditListingAvailabilityPanel
          allExceptions={allExceptions}
          weeklyExceptionQueries={weeklyExceptionQueries}
          monthlyExceptionQueries={monthlyExceptionQueries}
          onFetchExceptions={onFetchExceptions}
          onAddAvailabilityException={onAddAvailabilityException}
          onDeleteAvailabilityException={onDeleteAvailabilityException}
          onNextTab={() =>
            redirectAfterDraftUpdate(
              listing.id,
              params,
              tab,
              marketplaceTabs,
              history,
              routeConfiguration
            )
          }
          config={config}
          history={history}
          routeConfiguration={routeConfiguration}
          {...panelProps(AVAILABILITY)}
        />
      );
    }
    case PHOTOS: {
      return (
        <EditListingPhotosPanel
          {...panelProps(PHOTOS)}
          listingImageConfig={config.layout.listingImage}
          images={images}
          onImageUpload={onImageUpload}
          onRemoveImage={onRemoveImage}
        />
      );
    }
    case CATEGORY: {
      return <EditListingCategoryPanel {...panelProps(CATEGORY)} config={config} />;
    }
    case SUBSCRIPTION_PLAN: {
      return <EditListingSubscriptinPlan {...panelProps(SUBSCRIPTION_PLAN)} />;
    }
    default:
      return null;
  }
};

export default EditListingWizardTab;
